import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environments';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ConfigureSidemarkPricingComponent } from '@office/storage/configure-sidemark-pricing/configure-sidemark-pricing.component';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { StorageChargeType } from '@shared/enums/storage-type.enum';
import { ConfigureSidemarkPricingDialogData, SidemarkPricingConfig } from '@shared/models/domain/custom-views.model';
import type { Project } from '@shared/models/domain/wms.model';

@Component({
    selector: 'custom-formly-field-sidemark-pricing-config',
    template: `
    <div class="flex items-center gap-3">
      <button type="button" mat-stroked-button color="primary" (click)="openPricingModal()">
        {{ field?.props?.label || 'Configure Pricing' }}
        @if (field?.props?.required) {
          <span class="text-red-600">*</span>
        }
      </button>
      @if (isConfigured) {
        <span class="text-sm font-medium text-green-700">Pricing Configured ✓</span>
      }
    </div>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton],
})
export class SidemarkPricingConfigTypeComponent extends FieldType<FieldTypeConfig> {
    private dialog = inject(MatDialog);
    private officeTenantsService = inject(OfficeTenantsService);
    private destroyRef = inject(DestroyRef);

    get isConfigured(): boolean {
        return !!(this.formControl?.value as SidemarkPricingConfig)?.configured;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    openPricingModal(): void {
        const websiteId = this.officeTenantsService.getTenantDetailsBySlug()?.tenantId ?? environment.defaultWebsiteId;
        const data: ConfigureSidemarkPricingDialogData = {
          websiteId,
          config: this.formControl?.value as SidemarkPricingConfig,
          storageType: (this.model as Partial<Project>)?.['storageType'] ?? null,
          storageChargeType: (this.model as Partial<Project>)?.['storageChargeType'] ?? StorageChargeType.OneTime,
        };

        this.dialog.open(ConfigureSidemarkPricingComponent, {
            data,
            width: '780px',
            maxWidth: '95vw',
            disableClose: true,
            panelClass: 'sidemark-pricing',
        }).afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result: SidemarkPricingConfig | undefined) => {
                if (!result?.configured) return;
                this.formControl.setValue(result);
                this.formControl.markAsTouched();
                this.formControl.markAsDirty();
            });
    }
}
